import { ref, Ref } from 'vue';
import { useStoryblokApi, ISbStoryData, ISbStories,  ISbResult, } from '@storyblok/vue';
import { TemplateArticleStoryblok } from '@/types';
import useConfig from '@/composables/useConfig';

type ErrorObj = {
  status: number;
  message: string;
  response: string;
};
interface ArticleProps {
  postsPerPage: number;
  currentPage: number;
  searchTerm?: string;
  filter?: object;
  delay?: boolean;
}
type State = 'INIT' | 'LOADING' | 'RESULTS' | 'NO_RESULTS' | 'ERROR';

export default function useArticles() {
  const storyblokApi = useStoryblokApi();
  const { brand } = useConfig();

  const stories: Ref<ISbStoryData<TemplateArticleStoryblok>[] | undefined> =
    ref(undefined);
  const error: Ref<ErrorObj | null> = ref(null);
  const totalItems = ref<number>(0);
  const state: Ref<State> = ref('INIT');

  const fetchArticles = async ({
    postsPerPage,
    currentPage,
    searchTerm,
    filter,
    delay,
  }: ArticleProps): Promise<ISbStoryData<TemplateArticleStoryblok>[]> => {
    try {
      // fetch page data
      const pathToStory = `cdn/stories/`;
      const templateFilter = 'TemplateArticle';
      state.value = 'LOADING';
      // why the delay
      const response = (await delayedFunction<ISbResult>(
        () =>
          storyblokApi.get(pathToStory, {
            version: import.meta.env.VITE_STORYBLOK_VERSION,
            starts_with: `${brand.value}/news`,
            content_type: templateFilter,
            sort_by: 'content.date:desc',
            per_page: postsPerPage,
            page: currentPage,
            search_term: searchTerm,
            filter_query: filter || '',
          }),
        delay ? 500 : 0,
      )) as ISbStories;
      // Access the 'Total' header
      totalItems.value = parseInt(response.headers['total'] as string, 10);
      // Update stories with fetched data
      stories.value = response.data
        .stories as ISbStoryData<TemplateArticleStoryblok>[];

      state.value = 'RESULTS';
      if (!stories.value || stories.value.length === 0) {
        state.value = 'NO_RESULTS';
      }
      return stories.value;
    } catch (err) {
      const errObj: ErrorObj = JSON.parse(err as string);
      error.value = errObj;
      state.value = 'ERROR';
      throw errObj;
    }
  };
  const delays = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  async function delayedFunction<T>(
    callback: () => Promise<T>,
    delayTimeMs: number,
  ): Promise<T> {
    const startTime = Date.now();
    const response = await callback();
    const endTime = Date.now();
    const elapsedTime = endTime - startTime;
    if (elapsedTime < delayTimeMs) {
      const delayTime = delayTimeMs - elapsedTime;
      await delays(delayTime);
      return response;
    } else {
      return response;
    }
  }
  return {
    stories,
    totalItems,
    error,
    fetchArticles,
    state,
  };
}
